import { defineStore } from "pinia";
import client from "../httpClient/client";
import { useRootStore } from "./root";
import { useSearchStore } from "./searchModule";
import {
  BookingState,
  SearchFilter,
  BookingFieldGroup,
  BookingField,
  BookingContractResponse,
  Tariff,
  CalculationParticipant,
  MobileUploadState,
  TariffMarking,
} from "types";
import { normalizeCountryCollectionValue } from "@/utils/collectionValues";
import {
  normalizeTariffMarkings,
  upsertTariffMarkingInList,
} from "@/utils/tariffMarkings";
import { normalizeCompareTariffIds } from "@/utils/compareTariffIds";
import { rollbar } from "../../rollbar";

export const useBookingStore = defineStore("book", {
  state: (): BookingState =>
    <BookingState>{
      bookingLinksByKey: {},
      bookingId: "",
      bookingFormId: null,
      bookingAndSearchValues: {
        searchValues: {},
        policyHolderValues: {},
        tariffsValues: {},
        entityValues: [],
        paymentValues: {},
      },
      collectionType: {
        message: "",
      },
      disabledAddons: [],
      enabledAddons: [],
      preselectedAddons: [],
      bookingForms: [],
      addonOption: [],
      paymentData: {},
      errorModal: false,
      currentTariff: null,
      resourceTypeOptions: [],
      toggleMultiselect: [],
      paymentInterval: {},
      addressValidity: true,
      addressEntityValidity: [],
      legalDisclaimers: [],
      marketingDisclaimers: [],
      bookingFieldsValidity: {
        insured_entity: {},
        payment: {
          interval: {
            hasErrors: false,
            message: "Bitte wählen Sie eine Zahlungsoption",
          },
        },
      },
      copyPolicyHolderLabel: "",
      visibleBookingFields: {},
      visibleBookingGroups: {} as Record<string, boolean>,
      disabledBookingGroups: {} as Record<string, boolean>,
      policy_holder: {},
      insured_entity: {},
      payment_method: {},
      broker_info: {},
      payer: {},
      insuredEntityPriceArray: [],
      currentBookingPage: 1,
      isPolicyHolderInsured: true,
      currentTariffPrice: "",
      tariffPriceChanged: false,
      updatedFilters: {},
      isNextButtonDisabled: false,
      isNextButtonClicked: false,
      isValidatingBookingData: false,
      inuredEntityAddedRemoved: false,
      isToastVisible: false,
      differentPayer: false,
      disabledBankDetails: false,
      acceptMarketingAffiliate: false,
      firstRenderPostalCode: true,
      firstRenderAddress: true,
      allowPolicyHolderCopy: false,
      displaySearchMaskOnCheckPage: false,
      contractCodes: [],
      selectedAddons: [],
      selectedOptionAddons: [],
      missingPriceFilters: [],
      previousIBAN: "",
      IBAN: "",
      bankName: "",
      toastOptions: {
        content: "",
        type: "",
        offerJumpToFirstError: false,
      },
      verificationLevel: null,
      userType: "",
      preBookingInfo: [],
      additionalDocuments: [],
      copyPolicyHolderTargetInsuredEntityId: null,
      chosenPaymentMethod: "",
      bookingSession: null,
      resumedBookingSession: null,
      wizardNavigationOverlay: false,
      completingBooking: false,
      bookingInProgress: false,
      navigationTransitionLocked: false,
      token: "",
      calculationDetails: {
        title: "",
        note: "",
        ownerName: "",
        ownerEmail: "",
        shareWithBroker: false,
        participants: [],
        generalLink: "",
        ownerLink: "",
      },
      informParticipants: false,
      mobileUploads: {},
      tariffMarkings: [],
      compareTariffIds: [],
    },
  getters: {
    markedTariffIds: (state): string[] =>
      state.tariffMarkings.map((m) => m.tariff_id).filter(Boolean),
    tariffMarkingById:
      (state) =>
      (tariffId: string): TariffMarking | undefined => {
        const id = String(tariffId ?? "").trim();
        if (!id) return undefined;
        return state.tariffMarkings.find((m) => m.tariff_id === id);
      },
    sortedBookingForms: (state) => {
      return state.bookingForms
        .sort((formA: BookingFieldGroup, formB: BookingFieldGroup) => {
          return formA.position - formB.position;
        })
        .filter((form: BookingFieldGroup) => {
          return form.app_booking_fields.length;
        });
    },
    tariffForms: (state) => {
      return state.bookingForms.filter((form: BookingFieldGroup) => {
        return (
          form.app_booking_fields.length &&
          form.booking_field_group_type === "tariff"
        );
      });
    },
    insuredEntityForm: (state) => {
      return state.bookingForms.filter((form: BookingFieldGroup) => {
        return (
          form.app_booking_fields.length &&
          form.booking_field_group_type === "insured_entity"
        );
      });
    },
    /** Mirrors root store; exposed for CMS `window[field]` / conditions scripts (`state.language`, etc.). */
    language() {
      return useRootStore().language;
    },
    code() {
      return useRootStore().code;
    },
    completedBy() {
      return useRootStore().completedBy;
    },
    /** Same as `completedBy`; useful when conditions mirror query param naming (`completed_by`). */
    completed_by() {
      return useRootStore().completedBy;
    },
    /** Search mask slug from Tariff Manager (`search_forms.attributes.slug`). */
    searchFormSlug() {
      return useSearchStore().searchFormSlug;
    },
    /** From loaded search form (`search_forms.app_id` / TM app slug). */
    appId() {
      return useSearchStore().appId;
    },
    /** From loaded search form (`search_forms.app_name` display title). */
    appAlias() {
      return useSearchStore().appAlias;
    },
  },
  actions: {
    setpreviousIBAN(iban) {
      this.previousIBAN = iban;
    },
    setTariffBookingForms(forms: BookingFieldGroup[]) {
      this.bookingForms = forms;
    },
    setBookingFormId(id: number | string | null) {
      this.bookingFormId = id ?? null;
    },
    setPaymentInterval(interval) {
      this.paymentInterval = interval;
      if (interval?.id) {
        this.tariffPriceChanged = true;
      }
    },
    setMobileUploadState(payload: {
      key: string;
      uploadFieldId: number;
      values: Partial<Omit<MobileUploadState, "uploadFieldId">>;
    }) {
      const existing = this.mobileUploads[payload.key];

      const defaultState: MobileUploadState = {
        uploadFieldId: payload.uploadFieldId,
        token: "",
        qrUrl: "",
        expiresAt: "",
        loading: false,
        status: "idle",
        isOpen: false,
        completedFile: null,
      };

      this.mobileUploads[payload.key] = {
        ...defaultState,
        ...(existing ?? {}),
        ...payload.values,
        uploadFieldId: payload.uploadFieldId,
      };
    },
    setBookingSession(session) {
      this.bookingSession = session;
      if (session && typeof session === "object") {
        const sessionRecord = session as {
          tariff_markings?: unknown;
          compare_tariff_ids?: unknown;
          booking_session?: {
            tariff_markings?: unknown;
            compare_tariff_ids?: unknown;
          };
        };
        const markings =
          sessionRecord.tariff_markings !== undefined
            ? sessionRecord.tariff_markings
            : sessionRecord.booking_session?.tariff_markings;
        if (markings !== undefined) {
          this.tariffMarkings = normalizeTariffMarkings(markings);
        }
        const compareIds =
          sessionRecord.compare_tariff_ids !== undefined
            ? sessionRecord.compare_tariff_ids
            : sessionRecord.booking_session?.compare_tariff_ids;
        if (compareIds !== undefined) {
          this.compareTariffIds = normalizeCompareTariffIds(compareIds);
        }
      }
      const rawCode =
        session &&
        typeof session === "object" &&
        (session as { metadata?: { code?: unknown } }).metadata?.code;
      if (
        rawCode !== undefined &&
        rawCode !== null &&
        String(rawCode).trim() !== ""
      ) {
        useRootStore().setCode(String(rawCode).toUpperCase());
      }
    },
    setTariffMarkings(markings: TariffMarking[] | unknown) {
      this.tariffMarkings = normalizeTariffMarkings(markings);
    },
    setCompareTariffIds(ids: string[] | unknown) {
      this.compareTariffIds = normalizeCompareTariffIds(ids);
    },
    upsertTariffMarking(marking: TariffMarking) {
      this.tariffMarkings = upsertTariffMarkingInList(
        this.tariffMarkings,
        marking,
      );
    },
    removeTariffMarking(tariffId: string) {
      const id = String(tariffId ?? "").trim();
      if (!id) return;
      this.tariffMarkings = this.tariffMarkings.filter(
        (m) => m.tariff_id !== id,
      );
    },
    removeMobileUploadState(key: string) {
      delete this.mobileUploads[key];
    },
    resetMobileUploads() {
      this.mobileUploads = {};
    },
    setWizardNavigationOverlay(active: boolean) {
      this.wizardNavigationOverlay = active;
    },
    setCompletingBooking(active: boolean) {
      this.completingBooking = active;
    },
    setBookingInProgress(active: boolean) {
      this.bookingInProgress = active;
    },
    setNavigationTransitionLocked(active: boolean) {
      this.navigationTransitionLocked = active;
    },
    setCalculationDetails(payload: {
      title?: string;
      note?: string;
      ownerName?: string;
      ownerEmail?: string;
      shareWithBroker?: boolean;
      participants?: CalculationParticipant[];
    }) {
      this.calculationDetails = {
        ...this.calculationDetails,
        ...payload,
        ownerEmail: payload.ownerEmail ?? this.calculationDetails.ownerEmail,
        participants:
          payload.participants ?? this.calculationDetails.participants,
      };
    },
    addCalculationParticipant(
      participant: CalculationParticipant = {
        name: "",
        email: "",
        role: "",
      },
    ) {
      this.calculationDetails.participants.push(participant);
    },
    setCalculationParticipant(payload: {
      index: number;
      participant: Partial<CalculationParticipant>;
    }) {
      const participants = [...this.calculationDetails.participants];
      const currentParticipant = participants[payload.index] ?? {
        name: "",
        email: "",
        role: "",
      };
      participants[payload.index] = {
        ...currentParticipant,
        ...payload.participant,
      };
      this.calculationDetails.participants = participants;
    },
    deleteCalculationParticipant(index: number) {
      this.calculationDetails.participants.splice(index, 1);
    },
    resetCalculationDetails() {
      this.calculationDetails = {
        title: "",
        note: "",
        ownerEmail: "",
        ownerName: "",
        shareWithBroker: false,
        participants: [],
        generalLink: "",
        ownerLink: "",
      };
    },
    setBookingPaymentData(paymentData) {
      this.paymentData = paymentData;
    },
    setResourceTypeOptions(choice) {
      this.resourceTypeOptions = choice;
    },
    setBookingId(id) {
      this.bookingId = id;
    },
    setBookingAndSearchValues(policyHolder, tariff, entity, search, payment) {
      if (entity) {
        this.bookingAndSearchValues = {
          searchValues: search,
          policyHolderValues: policyHolder,
          tariffsValues: tariff,
          entityValues: entity,
          paymentValues: payment,
        };
      } else {
        this.bookingAndSearchValues = {
          searchValues: search,
          policyHolderValues: policyHolder,
          tariffsValues: tariff,
          entityValues: {},
          paymentValues: payment,
        };
      }
    },
    setPaymentMethod(val) {
      this.chosenPaymentMethod = val;
    },
    setPreBookingInfo(data) {
      this.preBookingInfo = data;
    },
    setToken(val) {
      this.token = val;
    },
    setAddressValidity(data) {
      this.addressValidity = data;
    },
    setLegalDisclaimers(data) {
      this.legalDisclaimers = data;
    },
    setMarketingDisclaimers(data) {
      this.marketingDisclaimers = data;
    },
    setCopyPolicyHolderLabel(data) {
      this.copyPolicyHolderLabel = data;
    },
    setInitialBookingFieldsValidity(data) {
      const fieldsValidity = {};
      let count = 0;

      if (data.arrayLength > 0) {
        while (count < data.arrayLength) {
          for (let i = 0; i < data.fields.length; i++) {
            if (data.fields[i].field_name === "address") {
              fieldsValidity[data.fields[i].id] = {
                streetName: {
                  hasErrors: false,
                  message: "",
                },
                streetNumber: {
                  hasErrors: false,
                  message: "",
                },
              };
            } else {
              fieldsValidity[data.fields[i].id] = {
                hasErrors: false,
                message: "",
              };
            }
          }
          if (data.appName) {
            this.bookingFieldsValidity.insured_entity[data.formName][count] =
              JSON.parse(JSON.stringify(fieldsValidity));
          } else {
            this.bookingFieldsValidity[data.formName][count] = JSON.parse(
              JSON.stringify(fieldsValidity),
            );
          }
          count++;
        }
      } else {
        for (let i = 0; i < data.fields.length; i++) {
          if (data.fields[i].field_name === "address") {
            fieldsValidity[data.fields[i].id] = {
              streetName: {
                hasErrors: false,
                message: "",
              },
              streetNumber: {
                hasErrors: false,
                message: "",
              },
            };
          } else if (data.fields[i].repeatable) {
            fieldsValidity[data.fields[i].id] = {
              hasErrors: false,
              message: "",
            };
            fieldsValidity[data.fields[i].repeat_field.id] = {
              hasErrors: false,
              message: "",
            };
          } else {
            fieldsValidity[data.fields[i].id] = {
              hasErrors: false,
              message: "",
            };
          }
        }
        this.bookingFieldsValidity[data.formName] = fieldsValidity;
      }
    },
    setDefaultPolicyHolderValues(fields: BookingField[]) {
      fields.forEach((field) => {
        if (
          this.policy_holder[field.field_name] === undefined ||
          this.policy_holder[field.field_name] === null
        ) {
          if (field.field_type === "collection") {
            this.policy_holder[field.field_name] = {
              label: "",
              id: "",
            };
          }
          if (field.field_type === "text") {
            this.policy_holder[field.field_name] = "";
          }
        }
      });
    },
    /** Same pattern as page 1 `setDefaultValuesNonEntityForms` / page 2 payment init: ensure `$state[form]` exists and fill missing field values (incl. boolean / html). */
    setDefaultFieldValues(form: string, fields: BookingField[]) {
      const state = this.$state as unknown as Record<string, unknown>;
      let slice = state[form];
      if (
        slice === undefined ||
        slice === null ||
        typeof slice !== "object" ||
        Array.isArray(slice)
      ) {
        slice = {};
        state[form] = slice;
      }
      const s = slice as Record<string, unknown>;
      fields.forEach((field: BookingField) => {
        const key = field.field_name;
        if (s[key] !== undefined && s[key] !== null) {
          return;
        }
        if (field.field_type === "collection") {
          s[key] = { label: "", id: "" };
        } else if (field.field_type === "text") {
          s[key] = "";
        } else if (field.field_type === "boolean") {
          s[key] = false;
        } else if (field.field_type === "html") {
          s[key] = String((field as { field_text?: string }).field_text ?? "");
        } else {
          s[key] = "";
        }
      });
    },
    setCurrentTariff(tariff: Tariff) {
      // Keep in sync with tariffModule: avoid sharing the search-list object reference.
      this.currentTariff = tariff ? { ...tariff } : tariff;
    },
    setCurrentBookingPage(page: number) {
      this.currentBookingPage = page;
      if (page >= 2) {
        this.tariffPriceChanged = true;
      }
    },
    setTariffPriceChanged(changed: boolean) {
      this.tariffPriceChanged = changed;
    },
    setVisibilityBookingFieldState(payload) {
      this.visibleBookingFields[payload.form] = payload.values;
    },
    setGroupVisibility(payload: { groupKey: string; visible: boolean }) {
      this.visibleBookingGroups[payload.groupKey] = payload.visible;
    },
    setDisabledGroups(payload: { groupKey: string; disabled: boolean }) {
      this.disabledBookingGroups[payload.groupKey] = payload.disabled;
    },
    resetVisibleBookingFieldState(form) {
      if (this.visibleBookingFields[form]) {
        this.visibleBookingFields[form] = [];
      }
      this.insured_entity[form] = [];
    },
    setVisibilityBookingFieldStateEntityForms(payload) {
      if (this.visibleBookingFields[payload.form] === undefined) {
        this.visibleBookingFields[payload.form] = [];
      }
      // Replace, never insert: re-running visibility for every entity on each
      // add used to grow this array quadratically (47 persons -> 1000+ rows).
      this.visibleBookingFields[payload.form][payload.index] = payload.values;
    },
    setCurrentTariffPrice(price: string) {
      this.currentTariffPrice = price;
    },
    setNextButtonState(bool: boolean) {
      this.isNextButtonDisabled = bool;
    },
    setNextButtonClickState(bool: boolean) {
      this.isNextButtonClicked = bool;
    },
    setValidatingBookingDataState(bool: boolean) {
      this.isValidatingBookingData = bool;
    },
    setInuredEntityAddedRemovedState(bool: boolean) {
      this.inuredEntityAddedRemoved = bool;
    },
    setVerificationLevel(level) {
      this.verificationLevel = level;
    },
    setMissingPriceFilters(filter: SearchFilter) {
      if (Array.isArray(filter)) {
        const filterType = filter.length > 0 ? filter[0].type : "";
        const filtered = this.missingPriceFilters.filter(
          (f) => f.type !== filterType,
        );
        filter.forEach((f) => {
          const exists = filtered.some(
            (existingFilter) => existingFilter.type === f.type,
          );
          if (!exists) {
            filtered.push(f);
          } else {
            const existingFilter = filtered.find(
              (existingFilter) => existingFilter.type === f.type,
            );
            if (existingFilter) {
              existingFilter.value = f.value;
            }
          }
        });

        this.missingPriceFilters = filtered;
      } else if (typeof filter === "object" && filter !== null) {
        const filterType = filter.type;
        const filtered = this.missingPriceFilters.filter(
          (f) => f.type !== filterType,
        );
        const exists = filtered.some(
          (existingFilter) => existingFilter.type === filterType,
        );
        if (!exists) {
          filtered.push(filter);
        }
        this.missingPriceFilters = filtered;
      }
    },
    setUserType(level) {
      this.userType = level;
    },
    setAddressFieldValuesFromPostCode(data) {
      let formInState = this.$state[data.formName];
      const isEntity = data.formName.includes("insured_entity");
      if (isEntity) {
        formInState = this.$state.insured_entity[data.formName][data.index];
      }
      formInState.country = normalizeCountryCollectionValue(data.country);
      formInState.countryName = formInState.country.label;

      // Set cityOptions and city value from API response
      if (
        data.addressRes.cities &&
        Array.isArray(data.addressRes.cities) &&
        data.addressRes.cities.length > 0
      ) {
        formInState.cityOptions = data.addressRes.cities;

        // Extract city string from array (could be string array or object array)
        let cityValue = "";
        if (typeof data.addressRes.cities[0] === "string") {
          cityValue = data.addressRes.cities[0];
        } else if (
          data.addressRes.cities[0] &&
          typeof data.addressRes.cities[0] === "object"
        ) {
          cityValue =
            data.addressRes.cities[0].id ||
            data.addressRes.cities[0].label ||
            "";
        }

        // For insured entities: only skip if copying is enabled AND city is already set
        if (
          isEntity &&
          this.policy_holder?.copy_policyholder_data &&
          formInState.city &&
          formInState.city !== ""
        ) {
          // Don't overwrite if copying is enabled and city already has a value
        } else {
          // Set city for policy holder or insured entity (when not copying or city is empty)
          if (cityValue && cityValue !== "") {
            formInState.city = cityValue;
          }
        }
      } else {
        // If no cities returned, set empty array (not empty object)
        formInState.cityOptions = [];
      }

      formInState.lat = data.addressRes.lat;
      formInState.lng = data.addressRes.lng;
      // Don't reset street fields if copying is enabled and they already have values
      const shouldResetStreets =
        !data.noReset &&
        !(
          isEntity &&
          this.policy_holder?.copy_policyholder_data &&
          (formInState.streetName ||
            formInState.streetNumber ||
            formInState.address)
        );
      if (shouldResetStreets) {
        formInState.streetName = "";
        formInState.streetNumber = "";
        formInState.address = "";
      }
    },
    setInsuredEntityPriceArray(priceArr: number[]) {
      this.insuredEntityPriceArray = priceArr;
    },
    setToastVisibility(boolean: boolean) {
      this.isToastVisible = boolean;
    },
    setToastOptions(options: {
      content: string;
      type: string;
      offerJumpToFirstError?: boolean;
    }) {
      this.toastOptions.content = options.content;
      this.toastOptions.type = options.type;
      this.toastOptions.offerJumpToFirstError =
        options.offerJumpToFirstError ?? false;
    },
    setBankBic(bic: string) {
      this.payment_method.bic = bic;
    },
    setBankName(bankName: string) {
      this.payment_method.bank_name = bankName;
    },
    setContractCodes(codes: BookingContractResponse[]) {
      this.contractCodes = codes;
    },
    setBankDetailsInputState(bool: boolean) {
      this.disabledBankDetails = bool;
    },
    setPostalCodeInitialRenderState(bool: boolean) {
      this.firstRenderPostalCode = bool;
    },
    setAddressInitialRenderState(bool: boolean) {
      this.firstRenderAddress = bool;
    },
    setSelectedAddons(addon) {
      this.selectedAddons.splice(addon.index, 1, addon.slug);
    },
    setSlectedOptionAddons(addon) {
      this.selectedOptionAddons = addon;
    },
    setAllowPolicyHolderCopy(bool: boolean) {
      this.allowPolicyHolderCopy = bool;
    },
    setCopyPolicyHolderTargetInsuredEntityId(id: number) {
      this.copyPolicyHolderTargetInsuredEntityId = id;
    },
    setDisplaySearchMaskOnCheckPage(bool: boolean) {
      this.displaySearchMaskOnCheckPage = bool;
    },
    resetAddressFieldValuesFromPostCode(data) {
      let formInState = this.$state[data.formName];
      const isEntity = data.formName.includes("insured_entity");
      if (isEntity) {
        formInState = this.insured_entity[data.formName][data.index];
      }
      formInState.country = { id: "", label: "" };
      formInState.countryName = "";
      formInState.streetName = "";
      formInState.streetNumber = "";
      formInState.lat = "";
      formInState.lng = "";
      formInState.address = "";
      formInState.city = "";
      formInState.cityOptions = [{ id: "", label: "" }];
    },
    resetStreetNameAndNumber(data) {
      let formInState = this.$state[data.formName];
      const isEntity = data.formName.includes("insured_entity");
      if (isEntity) {
        formInState = this.insured_entity[data.formName][data.index];
      }
      formInState.streetName = "";
      formInState.streetNumber = "";
      formInState.address = "";
    },
    resetBookingState() {
      const bookingLinksByKey = this.bookingLinksByKey;
      this.$reset();
      this.bookingLinksByKey = bookingLinksByKey;
    },
    convertFiltersToQuery(resourceFiltersArray) {
      return resourceFiltersArray
        .map((filterObj) => {
          const key = Object.keys(filterObj)[0];
          const value = filterObj[key];
          return `${key}=${value}`;
        })
        .join("&");
    },
    async fetchBookingForm(data) {
      const rootStore = useRootStore();
      const language = rootStore.language;
      const completedBy = rootStore.completedBy;
      const code = rootStore.code;

      const completedByPart = `&language=${language}&completed_by=${completedBy}&code=${code}`;
      const isLocalEnv = process.env.VUE_APP_TARIFF_MANAGER_URL !== undefined;
      const basePath = isLocalEnv
        ? process.env.VUE_APP_TARIFF_MANAGER_URL
        : "/";
      const searchParams = data.searchValues
        .map(
          (obj) =>
            `${encodeURIComponent(obj.type)}=${encodeURIComponent(obj.value)}`,
        )
        .join("&");
      const resourceFiltersQuery = this.convertFiltersToQuery(
        data.resourceFiltersArray,
      );
      let baseQuery = `application=${data.appName}&tariff_id=${data.tariffLegacyId}&customer_role=${data.completedBy}&search_token=${data.searchToken}&${searchParams}`;
      const resourceFiltersPart = resourceFiltersQuery
        ? `&${resourceFiltersQuery}`
        : "";
      const commonQueryPart = `&partner_token=${data.partnerToken}${completedByPart}`;
      if (data.bookingId) {
        baseQuery = `application=${data.appName}&tariff_id=${data.tariffLegacyId}&search_token=${data.searchToken}&vertrag_erneuern=${data.bookingId}&${searchParams}`;
      }
      if (data.contractRenewal !== "") {
        baseQuery = `application=${data.appName}&tariff_id=${data.tariffLegacyId}&search_token=${data.searchToken}&vertrag_erneuern=${data.contractRenewal}&${searchParams}`;
      }
      if (rootStore.brokerStatusAccepted) {
        if (rootStore.hasAParameter) {
          const query = `${baseQuery}${resourceFiltersPart}${commonQueryPart}&a=`;
          return client.getRequest(`${basePath}api/v1/booking_forms/?${query}`);
        } else {
          const query = `${baseQuery}${resourceFiltersPart}${commonQueryPart}&r=`;
          return client.getRequest(`${basePath}api/v1/booking_forms/?${query}`);
        }
      } else {
        const query = `${baseQuery}${resourceFiltersPart}${commonQueryPart}&r=${data.affiliateParam}`;
        return client.getRequest(`${basePath}api/v1/booking_forms/?${query}`);
      }
    },
    fetchBookingRef(contractId, searchToken) {
      const isLocalEnv = process.env.VUE_APP_TARIFF_MANAGER_URL !== undefined;
      const basePath = isLocalEnv
        ? process.env.VUE_APP_TARIFF_MANAGER_URL
        : "/";
      return new Promise((resolve, reject) => {
        const poll = async () => {
          try {
            const response = await client.getRequest(
              `${basePath}api/v1/bookings/${contractId}?search_token=${searchToken}`,
            );

            if (response.data.booking_reference !== null) {
              resolve(response);
            } else {
              setTimeout(poll, 5000);
            }
          } catch (error) {
            rollbar?.error(error as Error, {
              context: "bookingModule:pollBooking",
            });
            reject(error);
          }
        };
        poll();
      });
    },
    async validateBookingPage(payload, token) {
      const isLocalEnv = process.env.VUE_APP_TARIFF_MANAGER_URL !== undefined;
      const basePath = isLocalEnv
        ? process.env.VUE_APP_TARIFF_MANAGER_URL
        : "/";
      const response = await client.postRequest(
        `${basePath}api/v1/validations?search_token=${token}`,
        payload,
      );
      return response;
    },
    async fetchBookingLinks(payload) {
      const cacheKey = `${payload.app}:${payload.search_token}`;
      const cachedLinks = this.bookingLinksByKey[cacheKey];

      if (cachedLinks !== undefined) {
        return cachedLinks;
      }
      const isLocalEnv = process.env.VUE_APP_TARIFF_MANAGER_URL !== undefined;
      const basePath = isLocalEnv
        ? process.env.VUE_APP_TARIFF_MANAGER_URL
        : "/";
      const response = await client.getRequest(
        `${basePath}api/v1/links?application=${payload.app}&search_token=${payload.search_token}`,
      );
      const links = Array.isArray(response.data.data) ? response.data.data : [];

      const sortedLinks = [...links].sort((linkA, linkB) => {
        const positionA =
          Number(linkA?.attributes?.position ?? linkA?.position) || 0;
        const positionB =
          Number(linkB?.attributes?.position ?? linkB?.position) || 0;

        return positionA - positionB;
      });
      this.bookingLinksByKey[cacheKey] = sortedLinks;

      return sortedLinks;
    },
  },
});
